import {
  CarrierIcon,
  EventIcon,
  LocationIcon,
  PartnersIcon,
  PropertiesIcon,
  ReferenceIcon,
  VesselIcon,
} from 'components/common/Icons/gradientIcons';
import {
  FilterCheckbox,
  FilterSelectsGroupSection,
  // TransportModeFilter
} from 'components/core';
// TODO: Update import from core index (can't read default from undefined)
import TransportModeFilter from 'components/core/TransportModeFilter/TransportModeFilter';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { TFilterOptions } from 'utils/context/FilterContext/types';
import DK from 'utils/dictionaries/filter_dictionary';
import { TFilterConfig } from 'utils/types';

const shipmentIdentifiers = [
  DK.BILL_OF_LADING,
  DK.HOUSE_BILL_OF_LADING,
  DK.BOOKING_NUMBER,
  DK.CONTAINER_ID,
  DK.ORDER,
  DK.PRO,
  DK.TRACKING_NUMBER,
  DK.AIR_WAYBILL,
  DK.HOUSE_AIR_WAYBILL,
  DK.RAIL_CAR_ID,
  DK.WAYBILL,
  DK.TRAIN_NUMBER,
  DK.WAGON_ID,
  DK.SERIAL_NUMBER,
  DK.VEHICLE_IDENTIFICATION_NUMBER,
];

const carrierOptions = [
  DK.CARRIER_NAME,
  DK.CARRIER_IATA,
  DK.CARRIER_ICAO,
  DK.CARRIER_SCAC,
  DK.CARRIER_US_DOT_NUMBER,
  DK.CARRIER_MC_NUMBER,
];

const vesselOptions = [
  DK.VESSEL_IMO,
  DK.VESSEL_NAME,
  DK.VESSEL_CALL_SIGN,
  DK.VESSEL_MMSI,
  DK.VEHICLE_ID,
  DK.LICENSE_PLATE,
  DK.RAIL_CAR_ID,
  DK.TRAILER_ID,
];

const orderReferenceOptions = [
  DK.ORDER_IDENTIFIER_CONTRACT,
  DK.ORDER_IDENTIFIER_LOAD,
  DK.ORDER_IDENTIFIER_MANUFACTURER,
  DK.ORDER_IDENTIFIER_PROMOTION,
  DK.ORDER_IDENTIFIER_SEASON,
  DK.ORDER_IDENTIFIER_VENDOR,
  DK.ORDER_TAG_ANALYST,
  DK.ORDER_TAG_CARRIER,
  DK.ORDER_TAG_CATEGORY,
  DK.ORDER_TAG_COMMODITY,
  DK.ORDER_TAG_DEPARTMENT,
  DK.ORDER_TAG_DIRECTOR,
  DK.ORDER_TAG_FAMILY,
  DK.ORDER_TAG_GROUP,
  DK.ORDER_TAG_MANAGER,
  DK.ORDER_TAG_SECTOR,
];

const eventOne = [DK.ACTUAL_DATETIME];
const eventTwo = [DK.PLANNED_DATETIME, DK.ACTUAL_DATETIME];
const eventAll = [
  DK.ESTIMATED_DATETIME,
  DK.PLANNED_DATETIME,
  DK.ACTUAL_DATETIME,
];

const loadItemIdentifiers = [DK.SKU, DK.UPC];

const eventLocation = (lastFreeDay: boolean): TFilterOptions =>
  [
    DK.EVENT_STOP_TYPE,
    DK.EVENT_LOCATION_NAME,
    DK.EVENT_ADDRESS,
    DK.EVENT_CITY,
    DK.EVENT_STATE,
    DK.EVENT_COUNTRY,
    DK.EVENT_ZIP,
    DK.EVENT_PORT_CODE,
    ...(!lastFreeDay ? [DK.EVENT_AIRPORT_CODE] : []),
  ].map((opt) => ({
    variant: 'Autocomplete',
    option: [opt, [DK.EQUALS, DK.NOT_EQUALS], []],
  }));

const eventOption = (
  options: string[],
  lastFreeDay: boolean,
): TFilterOptions => {
  const locations = eventLocation(lastFreeDay);
  const opts: TFilterOptions = options.map((opt) => {
    const isPlanned = opt === DK.PLANNED_DATETIME;
    return {
      variant: 'TimeInput',
      option: [
        opt,
        [
          DK.WITHIN,
          !isPlanned ? DK.RUNNING_LATE : '',
          !isPlanned ? DK.RUNNING_EARLY : '',
          DK.IN_THE_NEXT,
          DK.IN_THE_LAST,
        ].filter(Boolean),
        [],
      ],
    };
  });

  return [...opts, ...locations];
};

const shipmentIdentifierOption = (options: string[]) =>
  options.map((opt) => ({
    variant: 'BulkInput',
    option: [opt, [DK.EQUALS, DK.NOT_EQUALS, DK.CONTAINS], []],
  }));

const autoCompleteOptionContains = (options: string[]): TFilterOptions =>
  options.map((opt) => ({
    variant: 'Autocomplete',
    option: [opt, [DK.EQUALS, DK.NOT_EQUALS, DK.CONTAINS], []],
  }));

const autoCompleteOption = (options: string[]): TFilterOptions =>
  options.map((opt) => ({
    variant: 'Autocomplete',
    option: [opt, [DK.EQUALS, DK.NOT_EQUALS], []],
  }));

const inputOption = (options: string[]): TFilterOptions =>
  options.map((opt) => ({
    variant: 'Input',
    option: [opt, [DK.EQUALS, DK.NOT_EQUALS, DK.CONTAINS], []],
  }));

const timeInputOption = (options: string[]): TFilterOptions =>
  options.map((opt) => ({
    variant: 'TimeInput',
    option: [opt, [DK.WITHIN, DK.IN_THE_NEXT, DK.IN_THE_LAST], []],
  }));

const loadItemOptions = (options: string[]): TFilterOptions => [
  ...inputOption(options),
  ...autoCompleteOption([DK.LOAD_ITEM_DESCRIPTION]),
];

const loadItemOptionsContains = (options: string[]): TFilterOptions => [
  ...inputOption(options),
  ...autoCompleteOptionContains([DK.LOAD_ITEM_DESCRIPTION]),
];

const autoCompleteOptionLocation = (
  options: string[],
  opts: string[],
  flag: boolean,
): TFilterOptions => {
  const mapper = flag ? autoCompleteOptionContains : autoCompleteOption;
  return [...autoCompleteOption(options), ...mapper(opts)].sort(
    (first, second) => {
      const prev = first.option[0];
      const next = second.option[0];
      if (prev === next) return 0;
      return prev < next ? -1 : 1;
    },
  );
};

const currentState = {
  title: DK.CURRENT_STATE,
  text: DK.CURRENT_STATE_SUBTITLE,
  Component: (
    <FilterCheckbox
      id={DK.CURRENT_STATE}
      collapsible
      options={[
        DK.UNKNOWN,
        DK.SCHEDULED,
        DK.IN_TRANSIT,
        DK.AT_STOP,
        DK.COMPLETED,
        DK.ACTION_REQUIRED,
        DK.IDLE,
      ]}
    />
  ),
};

const properties = (featureExc1365: boolean) => ({
  title: DK.PROPERTIES,
  text: DK.PROPERTIES_SUBTITLE,
  Component: (
    <FilterSelectsGroupSection
      title={DK.PROPERTIES}
      buttonText={DK.ADD_PROPERTY}
      placeholder={DK.SELECT_PROPERTY}
      icons={{
        default: PropertiesIcon,
        [DK.CARRIER]: CarrierIcon,
        [DK.VESSEL_OR_VEHICLE_ID]: VesselIcon,
        [DK.REFERENCE_KEY]: ReferenceIcon,
        [DK.SHARED_VISIBILITY]: PartnersIcon,
      }}
      options={
        [
          {
            value: DK.IDENTIFIER,
            config: shipmentIdentifierOption(shipmentIdentifiers),
          },
          {
            value: DK.CARRIER,
            config: featureExc1365
              ? autoCompleteOptionContains(carrierOptions)
              : autoCompleteOption(carrierOptions),
          },
          {
            value: DK.VESSEL_OR_VEHICLE_ID,
            config: featureExc1365
              ? autoCompleteOptionContains(vesselOptions)
              : autoCompleteOption(vesselOptions),
          },
          {
            value: DK.REFERENCE_KEY,
            startVariant: 'Autocomplete',
            config: [
              {
                variant: 'Autocomplete',
                option: [
                  '',
                  featureExc1365
                    ? [DK.EQUALS, DK.NOT_EQUALS, DK.CONTAINS]
                    : [DK.EQUALS, DK.NOT_EQUALS],
                  [],
                ],
              },
            ],
          },
          {
            value: DK.SHARED_VISIBILITY,
            config: featureExc1365
              ? autoCompleteOptionContains([
                  DK.SHARING_RECIPIENT_TENANT_NAME,
                  DK.SHARING_ORIGINATOR_TENANT_NAME,
                ])
              : autoCompleteOption([
                  DK.SHARING_RECIPIENT_TENANT_NAME,
                  DK.SHARING_ORIGINATOR_TENANT_NAME,
                ]),
          },
          // TODO remove 'any' cast with flag'
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ].filter(Boolean) as any
      }
    />
  ),
});

const modeOfShipment = {
  title: DK.MODE_OF_SHIPMENT,
  text: DK.MODE_OF_SHIPMENT_SUBTITLE,
  Component: <TransportModeFilter />,
};

const connectivity = {
  title: DK.CONNECTIVITY,
  text: DK.CONNECTIVITY_SUBTITLE,
  Component: (
    <FilterCheckbox
      id={DK.CONNECTIVITY}
      collapsible
      options={[
        DK.TRACKING_START,
        DK.TRACKING_STOP,
        DK.TRACKING_FAILED,
        DK.UNKNOWN_EQUIPMENT_IDENTIFIER,
        DK.MISSING_EQUIPMENT_IDENTIFIER,
        DK.TRACKING_END_BY_USER,
        DK.TRACKING_END_DUE_TO_TIMEOUT,
        DK.DRIVER_DENY_TRACKING,
      ]}
    />
  ),
};

const relatedEntity = (featureExc1365: boolean) => ({
  title: DK.RELATED_ENTITY_ID,
  text: DK.RELATED_ENTITY_SUBTITLE,
  Component: (
    <FilterSelectsGroupSection
      title={DK.RELATED_ENTITY_ID}
      placeholder={DK.SELECT_ATTRIBUTES}
      buttonText={DK.ADD_ATTRIBUTES}
      icons={{
        default: PropertiesIcon,
        [DK.ORDER_TAGS]: ReferenceIcon,
      }}
      options={[
        {
          value: DK.ORDER_IDENTIFIER,
          config: inputOption([
            DK.PURCHASE_ORDER,
            DK.SALES_ORDER,
            DK.ADVANCED_SHIPMENT_NOTICE,
            DK.WAREHOUSE_MOVEMENT_ORDER,
          ]),
        },
        {
          value: DK.ORDER_TAGS,
          config: featureExc1365
            ? autoCompleteOptionContains(orderReferenceOptions)
            : autoCompleteOption(orderReferenceOptions),
        },
        {
          value: DK.ORDER_DATE,
          config: timeInputOption([
            DK.SUPPLIER_READY_START_DATETIME,
            DK.ORIGINAL_DELIVERY_START_DATETIME,
          ]),
        },
        {
          value: DK.LOAD_ITEM,
          config: featureExc1365
            ? loadItemOptionsContains(loadItemIdentifiers)
            : loadItemOptions(loadItemIdentifiers),
        },
      ]}
    />
  ),
});

const LocationComponentProps = (featureExc1365: boolean) => {
  const stopConfig = autoCompleteOptionLocation(
    [
      DK.STOP_AIRPORT_CODE,
      DK.STOP_COUNTRY,
      DK.STOP_PORT_CODE,
      DK.STOP_STATE,
      DK.STOP_TYPE,
      DK.STOP_ZIP,
    ],
    [DK.STOP_ADDRESS, DK.STOP_CITY, DK.STOP_LOCATION_NAME],
    featureExc1365,
  );

  return [
    {
      value: DK.ORIGIN,
      config: autoCompleteOptionLocation(
        [
          DK.ORIGIN_AIRPORT_CODE,
          DK.ORIGIN_COUNTRY,
          DK.ORIGIN_PORT_CODE,
          DK.ORIGIN_STATE,
          DK.STOP_TYPE,
          DK.ORIGIN_ZIP,
        ],
        [DK.ORIGIN_ADDRESS, DK.ORIGIN_CITY, DK.ORIGIN_LOCATION_NAME],
        featureExc1365,
      ),
    },
    {
      value: DK.DESTINATION,
      config: autoCompleteOptionLocation(
        [
          DK.DESTINATION_AIRPORT_CODE,
          DK.DESTINATION_COUNTRY,
          DK.DESTINATION_PORT_CODE,
          DK.DESTINATION_STATE,
          DK.STOP_TYPE,
          DK.DESTINATION_ZIP,
        ],
        [
          DK.DESTINATION_ADDRESS,
          DK.DESTINATION_CITY,
          DK.DESTINATION_LOCATION_NAME,
        ],
        featureExc1365,
      ),
    },
    { value: DK.CURRENT_STOP, config: stopConfig },
    { value: DK.ANY_STOP, config: stopConfig },
    { value: DK.ALL_STOP, config: stopConfig },
  ];
};

const LocationComponent = (featureExc1365: boolean) => (
  <FilterSelectsGroupSection
    title={DK.LOCATIONS}
    buttonText={DK.ADD_LOCATION}
    placeholder={DK.SELECT_LOCATION}
    icons={{ default: LocationIcon }}
    options={LocationComponentProps(featureExc1365)}
  />
);

const EventDateProps = [
  { value: DK.ARRIVAL_AT_STOP, config: eventOption(eventAll, false) },
  { value: DK.DEPARTURE_FROM_STOP, config: eventOption(eventAll, false) },
  { value: DK.LOAD, config: eventOption(eventTwo, false) },
  { value: DK.LOAD_ONTO_VEHICLE, config: eventOption(eventOne, false) },
  { value: DK.UNLOAD_FROM_VEHICLE, config: eventOption(eventTwo, false) },
  { value: DK.MANIFEST, config: eventOption(eventAll, false) },
  { value: DK.DELIVERY, config: eventOption(eventOne, false) },
  { value: DK.DISCHARGE, config: eventOption(eventAll, false) },
  { value: DK.AVAILABLE, config: eventOption(eventTwo, false) },
  { value: DK.DISPATCH, config: eventOption(eventTwo, false) },
  { value: DK.PICKED_UP, config: eventOption(eventTwo, false) },
  { value: DK.OUT_FOR_DELIVERY, config: eventOption(eventTwo, false) },
  { value: DK.FLIGHT_BOOKING, config: eventOption(eventOne, false) },
  { value: DK.CONSIGNEE_NOTIFY, config: eventOption(eventTwo, false) },
  { value: DK.RECEIVE_FROM_SHIPPER, config: eventOption(eventOne, false) },
  { value: DK.RETURN_TO_SENDER, config: eventOption(eventOne, false) },
  { value: DK.GATE_IN_EMPTY, config: eventOption(eventTwo, false) },
  { value: DK.GATE_IN_FULL, config: eventOption(eventTwo, false) },
  { value: DK.GATE_OUT_EMPTY, config: eventOption(eventTwo, false) },
  { value: DK.GATE_OUT_FULL, config: eventOption(eventTwo, false) },
  { value: DK.LAST_FREE_DAY, config: eventOption([DK.PLANNED_DATETIME], true) },
];

const EventDateComponent = () => (
  <FilterSelectsGroupSection
    title={DK.EVENTS_AND_DATES}
    buttonText={DK.ADD_EVENT}
    placeholder={DK.SELECT_EVENT}
    icons={{ default: EventIcon }}
    options={EventDateProps}
  />
);

const ShipmentFilters = ({
  flags: { featureExc1365 },
  permissions: { canViewOrders },
}: LDFlagSet): TFilterConfig => {
  return [
    {
      id: 'activityFilters',
      groupName: 'core.activityFilters',
      items: [
        {
          title: DK.EVENTS_AND_DATES,
          text: DK.EVENTS_AND_DATES_SUBTITLE,
          Component: EventDateComponent(),
        },
        currentState,
        connectivity,
      ],
    },
    {
      id: 'attributeFilters',
      groupName: 'core.attributeFilters',
      items: [
        {
          title: DK.LOCATION,
          text: DK.LOCATION_SUBTITLE,
          Component: LocationComponent(featureExc1365),
        },
        canViewOrders && relatedEntity(featureExc1365),
        properties(featureExc1365),
        modeOfShipment,
        {
          title: DK.ID_TYPE,
          text: DK.ID_TYPE_SUBTITLE,
          Component: (
            <FilterCheckbox
              id={DK.ID_TYPE}
              collapsible
              options={shipmentIdentifiers}
            />
          ),
        },
      ],
    },
  ]
    .filter(Boolean)
    .map((group) => {
      return Object.assign({}, group, { items: group.items.filter(Boolean) });
    }) as TFilterConfig;
};

export default ShipmentFilters;
